/**
 * EngageEmail — /engage/email
 * Guided AI email campaign composer with intent selection, preview shell, and history.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Mail, Send, Eye, EyeOff, Users, Clock,
  CheckCircle2, XCircle, AlertCircle, RotateCcw, Layers,
  Sparkles, X, MessageSquare, Tag, Star, RefreshCw, Edit, ChevronLeft,
  Building2, Image as ImageIcon, Upload, Trash2
} from 'lucide-react';
import { auth, db, storage } from '../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { collection, onSnapshot } from 'firebase/firestore';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCompany } from '../context/CompanyContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

// ─── Default header image ──────────────────────────────────────────────────────

const DEFAULT_HEADER_IMAGE = 'https://static.prod-images.emergentagent.com/jobs/71845aaf-1c78-4478-a0bc-30d4b7b55d1c/images/4fa1e041f558e8acda5af2b9e4f43195c9c683a5fa4e181143cd234077baa68c.png';



// ─── Intent configs ────────────────────────────────────────────────────────────

const INTENT_CONFIGS = [
  {
    id: 'quote_followup',
    label: 'Quote Follow-Up',
    description: 'Re-engage leads with open quotes',
    icon: MessageSquare,
    color: 'blue',
  },
  {
    id: 'seasonal_promo',
    label: 'Seasonal Promotion',
    description: 'Drive bookings with a timely offer',
    icon: Tag,
    color: 'orange',
  },
  {
    id: 'review_request',
    label: 'Review Request',
    description: 'Turn happy customers into reviews',
    icon: Star,
    color: 'yellow',
  },
  {
    id: 'reactivation',
    label: 'Reactivation',
    description: 'Win back customers who\'ve gone quiet',
    icon: RefreshCw,
    color: 'green',
  },
  {
    id: 'custom',
    label: 'Custom',
    description: 'Write exactly what you have in mind',
    icon: Edit,
    color: 'violet',
  },
];

const INTENT_COLOR_MAP = {
  blue:   { card: 'border-blue-200 bg-blue-50',   icon: 'bg-blue-100 text-blue-600',   sel: 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'   },
  orange: { card: 'border-orange-200 bg-orange-50', icon: 'bg-orange-100 text-orange-600', sel: 'border-orange-500 bg-orange-50 ring-2 ring-orange-200' },
  yellow: { card: 'border-yellow-200 bg-yellow-50', icon: 'bg-yellow-100 text-yellow-600', sel: 'border-yellow-500 bg-yellow-50 ring-2 ring-yellow-200' },
  green:  { card: 'border-green-200 bg-green-50',  icon: 'bg-green-100 text-green-600',  sel: 'border-green-500 bg-green-50 ring-2 ring-green-200'  },
  violet: { card: 'border-violet-200 bg-violet-50', icon: 'bg-violet-100 text-violet-600', sel: 'border-violet-500 bg-violet-50 ring-2 ring-violet-200' },
};

// ─── Improve action definitions ────────────────────────────────────────────────

const IMPROVE_ACTIONS = [
  { id: 'shorter',    label: 'Make shorter',       instruction: 'Make this email shorter. Cut it down by about 40%, keeping the core message. Remove filler sentences.' },
  { id: 'persuasive', label: 'More persuasive',    instruction: 'Make this email more persuasive. Strengthen the value proposition and the call-to-action.' },
  { id: 'casual',     label: 'More casual',        instruction: 'Rewrite this email with a warmer, more casual and friendly tone. Keep the same message.' },
  { id: 'urgency',    label: 'Add urgency',        instruction: 'Add a clear sense of urgency to this email. Include time-limited language or limited availability signals.' },
];

// ─── Email preview shell ────────────────────────────────────────────────────────

const EmailPreviewShell = ({ subject, body, headerImageOption = 'default', headerImageUrl = '' }) => {
  const previewBody = (body || '')
    .replace(/\{\{customer_name\}\}/g, 'John Smith')
    .replace(/\{\{first_name\}\}/g, 'John');

  const resolvedImageUrl =
    headerImageOption === 'default' ? DEFAULT_HEADER_IMAGE
    : headerImageOption === 'custom' ? headerImageUrl
    : null; // 'none'

  return (
    <div className="rounded-xl border border-gray-200 overflow-hidden shadow-sm bg-gray-50" data-testid="email-preview-shell">
      {/* Email chrome */}
      <div className="bg-white border-b border-gray-200 px-4 py-2.5 flex items-center gap-2">
        <div className="flex gap-1">
          <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
          <span className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
          <span className="w-2.5 h-2.5 rounded-full bg-green-400" />
        </div>
        <div className="flex-1 bg-gray-100 rounded px-3 py-1 text-xs text-gray-500 truncate ml-2">
          {subject || 'No subject'}
        </div>
      </div>

      {/* Email body */}
      <div className="bg-white mx-auto max-w-[560px] my-4 rounded-xl overflow-hidden shadow-sm border border-gray-100">
        {/* Header image OR orange band */}
        {resolvedImageUrl ? (
          <div className="relative">
            <img
              src={resolvedImageUrl}
              alt="Email header"
              className="w-full h-32 object-cover block"
              data-testid="preview-header-image"
            />
            <div className="h-1.5 bg-gradient-to-r from-orange-500 to-orange-400" />
          </div>
        ) : (
          <div className="h-2 bg-gradient-to-r from-orange-500 to-orange-400" />
        )}

        {/* Content */}
        <div
          className="px-8 py-6 prose prose-sm max-w-none text-gray-700 leading-relaxed"
          dangerouslySetInnerHTML={{ __html: previewBody || '<p class="text-gray-400">Email body will appear here...</p>' }}
        />

        {/* Footer */}
        <div className="px-8 py-4 bg-gray-50 border-t border-gray-100 text-xs text-gray-400 text-center">
          You received this email as a valued customer. <span className="underline cursor-pointer">Unsubscribe</span>
        </div>
      </div>
    </div>
  );
};

// ─── Status badge ──────────────────────────────────────────────────────────────


const STATUS_STYLES = {
  sent:    'bg-green-100 text-green-700',
  sending: 'bg-blue-100 text-blue-700',
  draft:   'bg-gray-100 text-gray-600',
  failed:  'bg-red-100 text-red-700',
};

// ─── History panel ─────────────────────────────────────────────────────────────

const EmailHistory = ({ companyId }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchHistory = useCallback(async () => {
    try {
      const token = await auth.currentUser?.getIdToken(false);
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/email/history`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json().catch(() => []);
      setCampaigns(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('[EngageEmail] History fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  if (loading) return <div className="text-sm text-gray-500 py-6 text-center">Loading history...</div>;

  if (!campaigns.length) {
    return (
      <div className="text-sm text-gray-500 py-6 text-center">
        No email campaigns sent yet.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {campaigns.map((c) => (
        <div
          key={c.id}
          className="bg-white rounded-xl border border-gray-200 p-4"
          data-testid={`email-campaign-${c.id}`}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-900 text-sm truncate">{c.name}</p>
              <p className="text-xs text-gray-500 truncate mt-0.5">{c.subject}</p>
              <p className="text-xs text-gray-400 mt-1">
                Audience: {c.audienceName || c.audienceId}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[c.status] || STATUS_STYLES.draft}`}>
                {c.status}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-100 text-xs text-gray-600">
            <span className="flex items-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
              {c.sentCount ?? 0} sent
            </span>
            {(c.failedCount > 0) && (
              <span className="flex items-center gap-1 text-red-600">
                <XCircle className="w-3.5 h-3.5" />
                {c.failedCount} failed
              </span>
            )}
            <span className="flex items-center gap-1 ml-auto text-gray-400">
              <Clock className="w-3.5 h-3.5" />
              {c.sentAt ? new Date(c.sentAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Guided AI Email Modal ──────────────────────────────────────────────────────

const GuidedEmailModal = ({ onClose, onGenerated, initialIntent = '', initialExtra = '', selectedAudienceName = '' }) => {
  const [step, setStep] = useState(initialIntent ? 'details' : 'intent');
  const [selectedIntent, setSelectedIntent] = useState(initialIntent || '');
  const [extraInput, setExtraInput] = useState(initialExtra || '');
  const [loading, setLoading] = useState(false);

  const handleSelectIntent = (intentId) => {
    setSelectedIntent(intentId);
    setStep('details');
  };

  const handleGenerate = async () => {
    if (!selectedIntent) { toast.error('Please select a campaign type'); return; }
    setLoading(true);
    try {
      const token = await auth.currentUser?.getIdToken(false);
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ai/email/generate`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ intent: selectedIntent, extra_input: extraInput.trim() }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.detail || 'Generation failed'); return; }
      onGenerated(data.subject, data.body, selectedIntent, extraInput.trim());
      toast.success('Email generated! Review and send when ready.');
      onClose();
    } catch (err) {
      console.error('[GuidedEmailModal] error:', err);
      toast.error('Failed to generate email');
    } finally {
      setLoading(false);
    }
  };

  const selectedConfig = INTENT_CONFIGS.find((c) => c.id === selectedIntent);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      data-testid="ai-email-modal"
      onClick={(e) => e.target === e.currentTarget && !loading && onClose()}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            {step === 'details' && (
              <button
                onClick={() => setStep('intent')}
                className="p-1 rounded hover:bg-gray-100 text-gray-400 mr-1"
                disabled={loading}
                data-testid="ai-modal-back-btn"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
            )}
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">
                {step === 'intent' ? "What's this campaign about?" : selectedConfig?.label}
              </h2>
              <p className="text-xs text-gray-500">
                {step === 'intent' ? 'Select a campaign type to get started' : selectedConfig?.description}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
            data-testid="ai-modal-close-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* ── Step 1: Intent selection ── */}
        {step === 'intent' && (
          <div className="px-6 py-5">
            <div className="grid grid-cols-2 gap-3">
              {INTENT_CONFIGS.map((cfg) => {
                const colors = INTENT_COLOR_MAP[cfg.color];
                const Icon = cfg.icon;
                return (
                  <button
                    key={cfg.id}
                    onClick={() => handleSelectIntent(cfg.id)}
                    className={`flex flex-col gap-2.5 p-4 rounded-xl border text-left transition-all hover:scale-[1.01] active:scale-[0.99] ${colors.card} hover:border-gray-300`}
                    data-testid={`intent-btn-${cfg.id}`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colors.icon}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{cfg.label}</p>
                      <p className="text-xs text-gray-500 mt-0.5 leading-snug">{cfg.description}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Step 2: Context + optional input ── */}
        {step === 'details' && (
          <div className="px-6 py-5 space-y-4">
            {/* Auto-context chips */}
            <div className="space-y-2">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Auto-included context</p>
              <div className="flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 rounded-full text-xs text-gray-600">
                  <Building2 className="w-3 h-3" />
                  Company name &amp; services
                </span>
                {selectedAudienceName ? (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-100 rounded-full text-xs text-blue-700">
                    <Users className="w-3 h-3" />
                    {selectedAudienceName}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 rounded-full text-xs text-gray-400 italic">
                    <Users className="w-3 h-3" />
                    No audience selected
                  </span>
                )}
              </div>
            </div>

            {/* Optional input */}
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-gray-700">
                Anything specific to mention?{' '}
                <span className="text-gray-400 font-normal text-xs">(optional)</span>
              </Label>
              <Textarea
                value={extraInput}
                onChange={(e) => setExtraInput(e.target.value)}
                placeholder={
                  selectedIntent === 'seasonal_promo'
                    ? 'e.g. 15% off pressure washing this April only'
                    : selectedIntent === 'custom'
                    ? 'Describe what you want the email to say...'
                    : 'e.g. mention our new gutter cleaning service'
                }
                rows={3}
                className="resize-none text-sm"
                autoFocus
                data-testid="ai-extra-input"
                onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleGenerate(); }}
              />
              <p className="text-xs text-gray-400">Press Ctrl+Enter to generate</p>
            </div>
          </div>
        )}

        {/* Footer (step 2 only) */}
        {step === 'details' && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50">
            <button
              onClick={() => setStep('intent')}
              disabled={loading}
              className="text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50"
            >
              Change type
            </button>
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={onClose} disabled={loading} data-testid="ai-modal-cancel-btn">
                Cancel
              </Button>
              <Button
                onClick={handleGenerate}
                disabled={loading}
                className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white"
                data-testid="ai-generate-btn"
              >
                {loading
                  ? <><RotateCcw className="w-4 h-4 mr-2 animate-spin" />Generating...</>
                  : <><Sparkles className="w-4 h-4 mr-2" />Generate Email</>
                }
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};



const EngageEmail = () => {
  const { companyId } = useCompany();
  const location = useLocation();
  const navigate = useNavigate();

  // Tab state: 'compose' | 'history'
  const [activeTab, setActiveTab] = useState('compose');

  // Audiences
  const [audiences, setAudiences] = useState([]);
  const [audiencesLoading, setAudiencesLoading] = useState(true);

  // Form
  const [form, setForm] = useState({
    campaignName: '',
    audienceId: '',
    subject: '',
    htmlBody: '',
    headerImageOption: 'default', // 'default' | 'none' | 'custom'
    headerImageUrl: '',
  });

  // UI state
  const [showPreview, setShowPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [showAiModal, setShowAiModal] = useState(false);
  const [lastIntent, setLastIntent] = useState('');
  const [lastExtra, setLastExtra] = useState('');
  const [improveLoading, setImproveLoading] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const fileInputRef = useRef(null);

  // ── Handle navigation state from Suggested Campaigns ──────────────────────
  useEffect(() => {
    const state = location.state;
    if (state?.openModal) {
      if (state.suggestedIntent) setLastIntent(state.suggestedIntent);
      if (state.suggestedExtra) setLastExtra(state.suggestedExtra);
      setShowAiModal(true);
      // Use window.history.replaceState to clear navigation state without triggering
      // React Router's navigation pipeline (which would cause EngageEmail to remount
      // and reset showAiModal back to false).
      window.history.replaceState({}, '', location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load audiences via Firestore real-time listener
  useEffect(() => {
    if (!companyId || !db) return;
    const ref = collection(db, 'companies', companyId, 'audiences');
    const unsub = onSnapshot(
      ref,
      (snap) => {
        setAudiences(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setAudiencesLoading(false);
      },
      (err) => {
        console.error('[EngageEmail] Audience listener error:', err);
        setAudiencesLoading(false);
      }
    );
    return unsub;
  }, [companyId]);

  const handleAiGenerated = (subject, body, intent, extra) => {
    setForm((f) => ({ ...f, subject, htmlBody: body }));
    if (intent) setLastIntent(intent);
    if (extra !== undefined) setLastExtra(extra);
    setShowPreview(true); // default to rendered preview after AI generation
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Please upload a JPG, PNG, or WebP image');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5 MB');
      return;
    }
    setUploadLoading(true);
    try {
      const uuid = Math.random().toString(36).slice(2);
      const ext = file.name.split('.').pop() || 'jpg';
      const storageRef = ref(storage, `companies/${companyId}/email_headers/${uuid}.${ext}`);
      const result = await uploadBytes(storageRef, file);
      const url = await getDownloadURL(result.ref);
      setForm((f) => ({ ...f, headerImageOption: 'custom', headerImageUrl: url }));
      toast.success('Image uploaded');
    } catch (err) {
      console.error('[EngageEmail] image upload error:', err);
      toast.error('Upload failed. Please try again.');
    } finally {
      setUploadLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleImprove = async (action) => {
    if (!form.htmlBody.trim()) { toast.error('Write or generate an email body first'); return; }
    setImproveLoading(action.id);
    try {
      const token = await auth.currentUser?.getIdToken(false);
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ai/email/improve`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ current_body: form.htmlBody, instruction: action.instruction }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.detail || 'Improvement failed'); return; }
      setForm((f) => ({ ...f, htmlBody: data.updated_body }));
      // keep preview mode so user sees the updated render
      toast.success(`Done — ${action.label.toLowerCase()}`);
    } catch (err) {
      console.error('[EngageEmail] Improve error:', err);
      toast.error('Failed to improve email');
    } finally {
      setImproveLoading(null);
    }
  };

  const handleSend = async () => {
    if (!form.audienceId) { toast.error('Please select an audience'); return; }
    if (!form.campaignName.trim()) { toast.error('Please enter a campaign name'); return; }
    if (!form.subject.trim()) { toast.error('Please enter a subject line'); return; }
    if (!form.htmlBody.trim()) { toast.error('Please enter an email body'); return; }

    // Resolve headerImageUrl to send to backend
    const headerImageUrl =
      form.headerImageOption === 'default' ? DEFAULT_HEADER_IMAGE
      : form.headerImageOption === 'custom' ? form.headerImageUrl
      : ''; // 'none' → empty string = no image

    setSending(true);
    setLastResult(null);
    try {
      const token = await auth.currentUser?.getIdToken(false);
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/campaigns/email/send`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            campaignName: form.campaignName,
            audienceId: form.audienceId,
            subject: form.subject,
            htmlBody: form.htmlBody,
            headerImageUrl,
          }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.detail || 'Failed to send campaign');
        return;
      }
      if (data.success) {
        setLastResult(data);
        toast.success(`Campaign sent to ${data.sendableCount} recipients!`);
        setForm({ campaignName: '', audienceId: '', subject: '', htmlBody: '', headerImageOption: 'default', headerImageUrl: '' });
        setShowPreview(false);
        setActiveTab('history');
      }
    } catch (err) {
      console.error('[EngageEmail] Send error:', err);
      toast.error('Failed to send campaign');
    } finally {
      setSending(false);
    }
  };

  const selectedAudience = audiences.find((a) => a.id === form.audienceId);

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Guided AI Modal */}
      {showAiModal && (
        <GuidedEmailModal
          onClose={() => setShowAiModal(false)}
          onGenerated={handleAiGenerated}
          initialIntent={lastIntent}
          initialExtra={lastExtra}
          selectedAudienceName={selectedAudience?.name || ''}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
            <Mail className="w-5 h-5 text-blue-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Email Campaigns</h1>
            <p className="text-sm text-gray-500">Send branded emails to your audience segments</p>
          </div>
        </div>
        <Button
          onClick={() => { setLastIntent(''); setShowAiModal(true); }}
          className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white flex items-center gap-2"
          data-testid="ai-generate-email-btn"
        >
          <Sparkles className="w-4 h-4" />
          Generate with AI
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
        {[{ id: 'compose', label: 'Compose', icon: Mail }, { id: 'history', label: 'History', icon: Clock }].map((tab) => (
          <button
            key={tab.id}
            className={`flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab.id ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab(tab.id)}
            data-testid={`email-tab-${tab.id}`}
          >
            <tab.icon className="w-3.5 h-3.5" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Compose Tab ── */}
      {activeTab === 'compose' && (
        <div className="space-y-5">
          <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-5">
            {/* Campaign name */}
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-gray-700">Campaign Name <span className="text-red-400">*</span></Label>
              <Input
                value={form.campaignName}
                onChange={(e) => setForm((f) => ({ ...f, campaignName: e.target.value }))}
                placeholder="e.g. Spring Promotion 2026"
                data-testid="campaign-name-input"
              />
            </div>

            {/* Audience */}
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-gray-700">Audience <span className="text-red-400">*</span></Label>
              <Select
                value={form.audienceId}
                onValueChange={(v) => setForm((f) => ({ ...f, audienceId: v }))}
              >
                <SelectTrigger data-testid="audience-select">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-gray-400" />
                    <SelectValue placeholder={audiencesLoading ? 'Loading audiences...' : 'Select audience'} />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {audiences.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name}
                      {a.contactCount != null && (
                        <span className="ml-2 text-gray-400 text-xs">({a.contactCount} contacts)</span>
                      )}
                    </SelectItem>
                  ))}
                  {!audiencesLoading && !audiences.length && (
                    <SelectItem value="_empty" disabled>No audiences found</SelectItem>
                  )}
                </SelectContent>
              </Select>
              {selectedAudience && (
                <p className="text-xs text-gray-500">
                  {selectedAudience.description || selectedAudience.filterSummary || ''}
                </p>
              )}
            </div>

            {/* Image Options */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <ImageIcon className="w-4 h-4 text-gray-400" />
                <Label className="text-sm font-medium text-gray-700">Header Image</Label>
              </div>
              <div className="flex flex-wrap gap-2" data-testid="image-options">
                {[
                  { value: 'default', label: 'Use default image' },
                  { value: 'none',    label: 'Remove image' },
                  { value: 'custom',  label: 'Upload image' },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      setForm((f) => ({ ...f, headerImageOption: opt.value }));
                      if (opt.value === 'custom') fileInputRef.current?.click();
                    }}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                      form.headerImageOption === opt.value
                        ? 'bg-orange-50 border-orange-300 text-orange-700'
                        : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                    data-testid={`image-option-${opt.value}`}
                  >
                    {opt.value === 'custom' && uploadLoading ? (
                      <><RotateCcw className="inline w-3 h-3 mr-1 animate-spin" />Uploading...</>
                    ) : opt.value === 'custom' && form.headerImageOption === 'custom' ? (
                      <><Upload className="inline w-3 h-3 mr-1" />Change image</>
                    ) : opt.value === 'none' ? (
                      <><Trash2 className="inline w-3 h-3 mr-1" />{opt.label}</>
                    ) : opt.value === 'custom' ? (
                      <><Upload className="inline w-3 h-3 mr-1" />{opt.label}</>
                    ) : opt.label}
                  </button>
                ))}
                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  data-testid="image-upload-input"
                  onChange={handleImageUpload}
                />
              </div>

              {/* Custom image thumbnail */}
              {form.headerImageOption === 'custom' && form.headerImageUrl && (
                <div className="relative w-full h-20 rounded-lg overflow-hidden border border-gray-200">
                  <img
                    src={form.headerImageUrl}
                    alt="Email header"
                    className="w-full h-full object-cover"
                    data-testid="custom-image-preview"
                  />
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, headerImageOption: 'default', headerImageUrl: '' }))}
                    className="absolute top-1 right-1 p-1 bg-white/90 rounded-full shadow text-gray-500 hover:text-red-500"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}

              {/* Default image preview */}
              {form.headerImageOption === 'default' && (
                <div className="w-full h-16 rounded-lg overflow-hidden border border-gray-200">
                  <img
                    src={DEFAULT_HEADER_IMAGE}
                    alt="Default header"
                    className="w-full h-full object-cover opacity-80"
                    data-testid="default-image-preview"
                  />
                </div>
              )}
            </div>

            {/* Subject */}
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-gray-700">Subject Line <span className="text-red-400">*</span></Label>
              <Input
                value={form.subject}
                onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                placeholder="e.g. Your spring cleaning estimate is ready"
                data-testid="subject-input"
              />
            </div>

            {/* Body — preview shell (default after AI gen) or raw editor */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium text-gray-700">
                  Email Body <span className="text-red-400">*</span>
                </Label>
                <button
                  type="button"
                  onClick={() => setShowPreview((v) => !v)}
                  className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700"
                  data-testid="preview-toggle-btn"
                >
                  {showPreview
                    ? <><EyeOff className="w-3.5 h-3.5" />Edit HTML</>
                    : <><Eye className="w-3.5 h-3.5" />Preview</>
                  }
                </button>
              </div>

              {showPreview ? (
                <EmailPreviewShell
                  subject={form.subject}
                  body={form.htmlBody}
                  headerImageOption={form.headerImageOption}
                  headerImageUrl={form.headerImageUrl}
                />
              ) : (
                <Textarea
                  value={form.htmlBody}
                  onChange={(e) => setForm((f) => ({ ...f, htmlBody: e.target.value }))}
                  placeholder={`<p>Hi {{customer_name}},</p>\n<p>Your message here...</p>`}
                  rows={10}
                  className="font-mono text-sm"
                  data-testid="body-editor"
                />
              )}

              <p className="text-xs text-gray-400">
                Merge tags: <code className="bg-gray-100 px-1 rounded">{'{{customer_name}}'}</code>{' '}
                <code className="bg-gray-100 px-1 rounded">{'{{first_name}}'}</code>
                {' · '}An unsubscribe footer is auto-appended to every email.
              </p>

              {/* AI iteration toolbar */}
              {form.htmlBody.trim() && (
                <div className="flex flex-wrap items-center gap-2 pt-1" data-testid="ai-toolbar">
                  <button
                    type="button"
                    onClick={() => setShowAiModal(true)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-violet-50 border border-violet-200 text-violet-700 hover:bg-violet-100 hover:border-violet-300 rounded-lg transition-colors"
                    data-testid="regenerate-btn"
                  >
                    <Sparkles className="w-3 h-3" />
                    Regenerate
                  </button>
                  <span className="text-gray-200 text-xs select-none">|</span>
                  {IMPROVE_ACTIONS.map((action) => (
                    <button
                      key={action.id}
                      type="button"
                      onClick={() => handleImprove(action)}
                      disabled={improveLoading !== null}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-gray-50 border border-gray-200 text-gray-600 hover:bg-gray-100 hover:border-gray-300 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                      data-testid={`improve-btn-${action.id}`}
                    >
                      {improveLoading === action.id && <RotateCcw className="w-3 h-3 animate-spin" />}
                      {action.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Send button */}
            <div className="flex items-center justify-between pt-2 border-t border-gray-100">
              {lastResult && (
                <div className="flex items-center gap-2 text-sm text-green-700">
                  <CheckCircle2 className="w-4 h-4" />
                  Last sent: {lastResult.sendableCount} emails dispatched
                </div>
              )}
              <Button
                onClick={handleSend}
                disabled={sending}
                className="ml-auto bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="send-campaign-btn"
              >
                {sending
                  ? <><RotateCcw className="w-4 h-4 mr-2 animate-spin" />Sending...</>
                  : <><Send className="w-4 h-4 mr-2" />Send Campaign</>
                }
              </Button>
            </div>
          </div>

          {/* Info box */}
          <div className="bg-blue-50 rounded-xl border border-blue-100 p-4 flex gap-3">
            <AlertCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-blue-700">
              Emails will only be sent to contacts in the selected audience that have a valid email address.
              An unsubscribe footer is automatically appended to all emails.
            </p>
          </div>
        </div>
      )}

      {/* ── History Tab ── */}
      {activeTab === 'history' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Layers className="w-4 h-4 text-gray-500" />
            <h2 className="text-sm font-semibold text-gray-700">Email Campaign History</h2>
          </div>
          <EmailHistory companyId={companyId} />
        </div>
      )}
    </div>
  );
};

export default EngageEmail;
